import { useEffect, useState, type FormEvent } from "react";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { DatePicker } from "@/components/ui/date-picker";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { formatDate } from "@/lib/date-utils";
import { formatCurrency } from "@/lib/utils";
import {
  isoDate,
  useDeleteVehicleServiceRecord,
  useSaveVehicleServiceRecord,
  type VehicleServiceRecord,
} from "@/hooks/useVehicles";

function parseOptionalNumber(value: string) {
  const trimmed = value.trim().replace(",", ".");
  if (!trimmed) return null;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : null;
}

function ServiceRecordDialog({
  open,
  onOpenChange,
  vehicleId,
  record,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  vehicleId: string;
  record: VehicleServiceRecord | null;
}) {
  const save = useSaveVehicleServiceRecord();
  const [serviceDate, setServiceDate] = useState("");
  const [mileage, setMileage] = useState("");
  const [workshop, setWorkshop] = useState("");
  const [description, setDescription] = useState("");
  const [cost, setCost] = useState("");

  useEffect(() => {
    if (!open) return;
    setServiceDate(
      isoDate(record?.serviceDate) ?? new Date().toISOString().slice(0, 10),
    );
    setMileage(record?.mileageKm?.toString() ?? "");
    setWorkshop(record?.workshop ?? "");
    setDescription(record?.description ?? "");
    setCost(record?.cost?.toString() ?? "");
  }, [open, record]);

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (!serviceDate || !description.trim()) return;
    const mileageKm = parseOptionalNumber(mileage);
    save.mutate(
      {
        id: record?.id,
        vehicleId,
        serviceDate,
        mileageKm: mileageKm === null ? null : Math.round(mileageKm),
        workshop: workshop.trim() || null,
        description: description.trim(),
        cost: parseOptionalNumber(cost),
      },
      { onSuccess: () => onOpenChange(false) },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>
              {record ? "Edit service entry" : "New service entry"}
            </DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="service-date">Date</Label>
              <DatePicker
                id="service-date"
                value={serviceDate}
                onChange={setServiceDate}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="service-mileage">Mileage (km)</Label>
              <Input
                id="service-mileage"
                inputMode="numeric"
                value={mileage}
                onChange={(event) => setMileage(event.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="service-workshop">Workshop</Label>
              <Input
                id="service-workshop"
                value={workshop}
                onChange={(event) => setWorkshop(event.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="service-cost">Cost (CHF)</Label>
              <Input
                id="service-cost"
                inputMode="decimal"
                value={cost}
                onChange={(event) => setCost(event.target.value)}
              />
            </div>
            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor="service-description">Work done</Label>
              <Textarea
                id="service-description"
                value={description}
                onChange={(event) => setDescription(event.target.value)}
                placeholder="e.g. Oil change, new brake pads front, winter tyres mounted"
                className="min-h-[120px]"
                data-testid="service-description-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={!serviceDate || !description.trim() || save.isPending}
              data-testid="save-service-record"
            >
              Save
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function VehicleServiceTab({
  vehicleId,
  records,
}: {
  vehicleId: string;
  records: VehicleServiceRecord[];
}) {
  const remove = useDeleteVehicleServiceRecord();
  const [dialog, setDialog] = useState<{
    open: boolean;
    record: VehicleServiceRecord | null;
  }>({ open: false, record: null });

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button
          onClick={() => setDialog({ open: true, record: null })}
          data-testid="add-service-record"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add service entry
        </Button>
      </div>

      {records.length === 0 ? (
        <div className="rounded-lg border py-8 text-center text-muted-foreground">
          No service entries yet.
        </div>
      ) : (
        <div className="overflow-hidden rounded-lg border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Mileage</TableHead>
                <TableHead>Workshop</TableHead>
                <TableHead>Work done</TableHead>
                <TableHead className="text-right">Cost</TableHead>
                <TableHead className="w-24" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {records.map((record) => (
                <TableRow
                  key={record.id}
                  data-testid={`service-record-${record.id}`}
                >
                  <TableCell className="whitespace-nowrap">
                    {formatDate(new Date(record.serviceDate))}
                  </TableCell>
                  <TableCell className="whitespace-nowrap tabular-nums">
                    {record.mileageKm != null
                      ? `${record.mileageKm.toLocaleString("de-CH")} km`
                      : "-"}
                  </TableCell>
                  <TableCell>{record.workshop || "-"}</TableCell>
                  <TableCell className="whitespace-pre-wrap">
                    {record.description}
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-right tabular-nums">
                    {record.cost != null ? formatCurrency(record.cost) : "-"}
                  </TableCell>
                  <TableCell>
                    <div className="flex justify-end gap-1">
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => setDialog({ open: true, record })}
                        aria-label="Edit service entry"
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => {
                          if (confirm("Delete this service entry?")) {
                            remove.mutate(record.id);
                          }
                        }}
                        aria-label="Delete service entry"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <ServiceRecordDialog
        open={dialog.open}
        onOpenChange={(open) => setDialog((prev) => ({ ...prev, open }))}
        vehicleId={vehicleId}
        record={dialog.record}
      />
    </div>
  );
}
